import { WarningCircleIcon } from '@phosphor-icons/react'
import * as Dialog from '@radix-ui/react-dialog'
import { useCallback } from 'react'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../services/toast-service.tsx'
import { useGetAnalyses, useGetScan, usePostAnalysisCancellation } from '../../utils/api-client/api-hooks.ts'
import type { ScanResponse } from '../../utils/api-client/api-schemas.ts'
import { formatDate } from '../../utils/date-utils.ts'
import { clearFirstBatchData } from '../../utils/first-batch-tracking.ts'
import { pluralize } from '../../utils/text-utils.ts'
import { DefaultButton } from '../buttons/default-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import { Spinner } from '../spinner.tsx'
import { BranchTag } from '../tags/branch-tag.tsx'
import { ToolIcon } from '../third-party-branding/tool-icon.tsx'
import * as styles from './delete-scan-modal.css.ts'

type CancelAnalysisModalProps = {
  scanId: string
  onClose: () => unknown
  onCancelSuccess?: () => unknown
}

export const CancelAnalysisModal: React.FC<CancelAnalysisModalProps> = ({
  scanId,
  onClose,
  onCancelSuccess = () => {},
}) => {
  const { addToast } = useToastService()
  const showMutationError = useShowMutationError({ prefix: 'Unable to cancel analysis' })
  const { mutate: cancelAnalysis, isPending } = usePostAnalysisCancellation({
    onSuccess: () => {
      // The first-batch localStorage marker keeps the lifecycle stuck at
      // `first-analysis-batch-in-progress` because `isFirstBatchComplete` counts only
      // COMPLETED, not CANCELLED. Clearing it here lets the UI resolve to
      // `initial-unanalyzed-scan` when no findings finished before cancel.
      clearFirstBatchData(scanId)
      onCancelSuccess()
      addToast({
        message: <>Analysis cancelled successfully</>,
        variant: 'success',
      })
    },
    onError: showMutationError,
  })
  const { data: scan, isLoading: isScanLoading } = useGetScan({ id: scanId })
  const { data: activeAnalyses } = useGetAnalyses({
    page: { number: 0, size: 1 },
    filters: { scanIds: [scanId], states: ['queued', 'in-progress'] },
  })
  const handleCancelAnalysis = useCallback(() => cancelAnalysis({ scanId }), [cancelAnalysis, scanId])

  if (isScanLoading) {
    return (
      <Dialog.Root open={true}>
        <Dialog.Portal>
          <Dialog.Overlay className={styles.alertDialogOverlay} />
          <Dialog.Content className={styles.alertDialogContent}>
            <Spinner size="24" label="Loading scan details..." />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    )
  }

  if (!scan) {
    return null
  }

  return (
    <CancelAnalysisModalPure
      scan={scan}
      activeAnalysisCount={activeAnalyses?.total}
      onClose={onClose}
      onCancelAnalysis={handleCancelAnalysis}
      isCancelling={isPending}
    />
  )
}

type CancelAnalysisModalPureProps = {
  scan: Pick<ScanResponse, 'id' | 'detector' | 'imported_at' | 'branch'>
  activeAnalysisCount?: number
  onClose: () => unknown
  onCancelAnalysis: () => unknown
  isCancelling?: boolean
}

export const CancelAnalysisModalPure: React.FC<CancelAnalysisModalPureProps> = ({
  scan,
  activeAnalysisCount,
  onClose,
  onCancelAnalysis,
  isCancelling = false,
}) => {
  return (
    <Dialog.Root open={true}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.alertDialogOverlay} />
        <Dialog.Content className={styles.alertDialogContent}>
          <Dialog.Title className={styles.alertDialogTitle}>
            <WarningCircleIcon className={styles.alertDialogIcon} size={32} weight="fill" />
            Cancel analysis?
          </Dialog.Title>
          <Dialog.Description className={styles.alertDialogDescription} asChild>
            <div>
              <p className={styles.alertDialogDescriptionParagraph}>
                {activeAnalysisCount !== undefined ? (
                  <>
                    You are about to cancel all active analysis across <strong>{activeAnalysisCount}</strong>{' '}
                    {pluralize(activeAnalysisCount, 'batch', 'batches')} on the following scan:
                  </>
                ) : (
                  <>You are about to cancel all active analysis on the following scan:</>
                )}
              </p>
              <div className={styles.alertDialogRepository} style={{ gap: '16px' }}>
                <ToolIcon tool={scan.detector} size="large+" withAriaLabel={true} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'left' }}>
                  <span>{formatDate({ date: scan.imported_at, type: 'mm/dd/yy' })}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <span style={{ fontStyle: 'italic' }}>
                      {formatDate({ date: scan.imported_at, type: 'time-with-zone' })}
                    </span>
                    <BranchTag branch={scan.branch} />
                  </span>
                </div>
              </div>
              <p className={styles.alertDialogDescriptionParagraph}>
                Results already produced will be kept, and anything still in progress will be lost and treated as
                unanalyzed. This action cannot be undone. Do you wish to proceed?
              </p>
            </div>
          </Dialog.Description>
          <div className={styles.alertDialogButtons}>
            <GhostButton type="secondary" onClick={onClose}>
              Continue analysis
            </GhostButton>
            <DefaultButton
              buttonType="destructive"
              onClick={onCancelAnalysis}
              state={isCancelling ? 'loading' : 'default'}
            >
              Cancel analysis
            </DefaultButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
